<template lang="pug">
.pulse-config
  .config-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.title') }}
  .total-metrics-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.all_segments_metrics') }}
      .metric-count {{ localConfig.total_metrics.length }} / {{ maxMetrics }}
    .metrics-tags
      aiq-tag(
        v-for="metricId in localConfig.total_metrics"
        :key="metricId"
        closable
        type="info"
        @close="onRemoveTotalMetric(metricId)"
      ) {{ getMetricLabel(metricId) }}
      .add-metric-dropdown(v-if="!isTotalMetricsMaxed")
        aiq-select(
          :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.add_metric')"
          clearable
          :filterable="selectConfig.filterable"
          :empty-values="selectConfig.emptyValues"
          :value-on-clear="''"
          @change="onAddTotalMetric"
          popper-class="pulse-config-dropdown"
        )
          aiq-option(
            v-for="metric in availableMetricsForTotal"
            :key="metric.id"
            :label="metric.label"
            :value="metric.id"
          )
      .max-reached(v-else) {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.selection_limit_reached') }}

  PulseConfigSegment(
    v-for="(segment, index) in localConfig.segments"
    :key="index"
    :segment="segment"
    :index="index"
    :availableSegments="availableSegmentsByIndex[index]"
    :availableMetrics="availableMetrics"
    :maxMetrics="maxMetrics"
    @update="onSegmentUpdate"
  )

  .action-buttons
    aiq-button(@click="onCancel") {{ $t('global.cancel') }}
    aiq-button(@click="onSave" :disabled="!isDataChanged" type="primary") {{ $t('global.save') }}
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PulseConfigSegment from './components/PulseConfigSegment.vue';

const DEFAULT_SEGMENTS = [
  { segment_names: [], metrics: [] },
  { segment_names: [], metrics: [] },
];

const DEFAULT_CONFIG = {
  total_metrics: [],
  segments: DEFAULT_SEGMENTS,
};

export default {
  name: 'PulseConfig',
  components: {
    PulseConfigSegment,
  },
  props: {
    config: {
      type: Object,
      default: () => DEFAULT_CONFIG,
    },
    segments: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save'],
  data() {
    return {
      selectConfig: {
        filterable: true,
        emptyValues: [''],
      },
      localConfig: cloneDeep(this.config),
      metricIds: [
        'active_unique_customers',
        'conversations',
        'messages',
        'first_response_time',
        'agent_first_response_time',
        'handle_time',
        'fully_automated_conversations',
        'partially_automated_conversations',
        'sales_leads',
        'abandonment_rate',
        'relationship_length',
        'primary_customer_retention_rate',
        'feedback_score',
      ],
      maxMetrics: 5,
    };
  },
  computed: {
    availableMetrics() {
      return this.metricIds.map(id => ({
        id,
        label: this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${id}`),
      }));
    },
    isDataChanged() {
      return !isEqual(this.localConfig, this.config);
    },
    availableMetricsForTotal() {
      return this.availableMetrics.filter(
        metric => !this.localConfig.total_metrics.includes(metric.id),
      );
    },
    isTotalMetricsMaxed() {
      return this.localConfig.total_metrics.length >= this.maxMetrics;
    },
    // Memoized available segments for each index
    availableSegmentsByIndex() {
      return this.localConfig.segments.map((_, index) => {
        const otherIndex = index === 0 ? 1 : 0;
        const otherSelectedSegments = this.localConfig.segments[otherIndex]?.segment_names || [];
        const currentSelectedSegments = this.localConfig.segments[index]?.segment_names || [];
        return this.segments.filter(
          seg => !otherSelectedSegments.includes(seg) || currentSelectedSegments.includes(seg),
        );
      });
    },
  },
  watch: {
    config: {
      handler(newVal) {
        if (newVal) {
          this.localConfig = cloneDeep(newVal);
        }
      },
      deep: true,
    },
  },
  methods: {
    getMetricLabel(metricId) {
      const metric = this.availableMetrics.find(m => m.id === metricId);
      return metric ? metric.label : metricId;
    },

    onAddTotalMetric(metricId) {
      if (metricId && !this.localConfig.total_metrics.includes(metricId) && !this.isTotalMetricsMaxed) {
        this.localConfig.total_metrics.push(metricId);
      }
    },

    onRemoveTotalMetric(metricId) {
      this.localConfig.total_metrics = this.localConfig.total_metrics.filter(
        id => id !== metricId,
      );
    },

    onSegmentUpdate(index, updatedSegment) {
      this.localConfig.segments[index] = { ...updatedSegment };
    },

    onSave() {
      const cleanedSegments = (this.localConfig.segments || DEFAULT_CONFIG.segments).map(segment => ({
        segment_names: segment.segment_names || [],
        metrics: segment.metrics || [],
      }));

      const cleanConfig = {
        total_metrics: this.localConfig.total_metrics || DEFAULT_CONFIG.total_metrics,
        segments: cleanedSegments,
      };
      this.$emit('save', cleanConfig);
    },

    onCancel() {
      this.localConfig = cloneDeep(this.config);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

.pulse-config {
  padding: 15px;
  background-color: $aiq-bgc-message-input;
  border-radius: 4px;
}

.config-label {
  font-size: 12px;
  font-weight: 600;
  color: $aiq-color-text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 15px;
}

.total-metrics-section {
  width: 100%;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $aiq-color-text-primary;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

.metrics-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  min-height: 32px;

  ::v-deep .aiq-tag {
    margin: 0;
    background-color: $aiq-color-active;
    border-color: $aiq-color-active;
    color: white;
    height: 38px;
    padding: 0 18px;
    border-radius: 19px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    line-height: normal;
    font-weight: 500;

    &:hover {
      background-color: darken($aiq-color-active, 10%);
      border-color: darken($aiq-color-active, 10%);
    }

    .el-tag__close {
      color: white;
      margin-left: 8px;
      margin-top: 0;
      display: flex;
      align-items: center;
    }
  }
}

.add-metric-dropdown {
  display: inline-block;
  min-width: 180px;
  position: relative;

  ::v-deep {
    .el-input--suffix .el-input__inner {
      padding-right: 30px;
    }
  }
}

.max-reached {
  color: $aiq-color-text-muted;
  font-size: 13px;
  padding: 8px 0;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}
</style>

<style lang="scss">
@import "../../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse-config {
  background-color: $aiq-bgc-sidebar-item-hover;

  .config-label {
    color: $aiq-color-text-muted;
  }

  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .total-metrics-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  ::v-deep .el-input__inner {
    color: white;
  }

  .metric-count {
    color: white;
  }

  .max-reached {
    color: white;
  }

  .action-buttons {
    border-top-color: $aiq-color-border-dark;
  }
}

.pulse-segment-checkbox-dropdown {
  .el-select-dropdown__item {
    padding: 8px 12px;
    height: auto;
    line-height: normal;

    &.selected::after,
    &.is-selected::after {
      display: none;
      content: none;
    }

    .el-icon {
      display: none;
    }

    .checkbox-option {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;

      .el-checkbox {
        pointer-events: none;
        margin-right: 0;
        height: auto;

        .el-checkbox__input {
          display: flex;
          align-items: center;
        }

        .el-checkbox__label {
          display: none;
        }
      }

      .option-label {
        color: inherit;
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
}

[data-theme="dark"] {
  .pulse-config-dropdown .el-select-dropdown__item {
    color: white;
  }

  .pulse-segment-checkbox-dropdown .el-select-dropdown__item .checkbox-option .option-label {
    color: white;
  }
}
</style>
